/* import __COLOCATED_TEMPLATE__ from './email-follow-up.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Fin from 'embercom/models/operator/visual-builder/step/fin';
import { getOperatorName, getOperatorAvatarUrl } from 'embercom/utils/operator-identity-utils';

type EmailFollowUpOption = 'follow_up' | 'no_follow_up';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const DEFAULT_FOLLOW_UP_SECONDS = 24 * HOUR;

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    step: Fin;
    isReadOnly: boolean;
    targetChannel?: string;
  };
}

export default class EmailFollowUp extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;

  get operatorName(): string {
    return getOperatorName(this.appService);
  }

  get operatorAvatarUrl(): string | null {
    return getOperatorAvatarUrl(this.appService);
  }

  get titleText(): string {
    switch (this.args.targetChannel) {
      case 'zendesk_ticket':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.title-zendesk-ticket',
        );
      case 'salesforce_case':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.title-salesforce-case',
        );
      case 'freshdesk_ticket':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.title-freshdesk-ticket',
        );
      case 'hubspot_email_conversation':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.title-hubspot-email-conversation',
        );
      case 'email':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.title',
        );
      default:
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.title-generic',
        );
    }
  }

  get selectedOption(): EmailFollowUpOption {
    return (this.args.step?.emailFollowUpType as EmailFollowUpOption) || 'follow_up';
  }

  set selectedOption(value: EmailFollowUpOption) {
    this.args.step.emailFollowUpType = value;
  }

  get effectiveEmailFollowUpTimer(): number {
    return this.args.step.emailFollowUpTimer ?? DEFAULT_FOLLOW_UP_SECONDS;
  }

  get emailFollowUpDurationSummary(): string {
    let timer = this.effectiveEmailFollowUpTimer;
    let days = Math.floor(timer / DAY);
    let hours = Math.floor((timer % DAY) / HOUR);
    let minutes = Math.floor(((timer % DAY) % HOUR) / MINUTE);

    let parts: string[] = [];
    if (days > 0) {
      parts.push(
        this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.days',
          { count: days },
        ),
      );
    }
    if (hours > 0) {
      parts.push(
        this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.hours',
          { count: hours },
        ),
      );
    }
    if (minutes > 0) {
      parts.push(
        this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.minutes',
          { count: minutes },
        ),
      );
    }

    if (parts.length === 0) {
      return this.intl.t(
        'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.empty',
      );
    }

    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.after',
      { duration: parts.join(', ') },
    );
  }

  get options() {
    return [
      {
        value: 'no_follow_up' as const,
        translationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-no-follow-up',
        subtitleTranslationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-no-follow-up-subtitle',
      },
      {
        value: 'follow_up' as const,
        translationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-follow-up',
        subtitleTranslationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-follow-up-subtitle',
      },
    ];
  }

  @action
  setSelectedOption(option: EmailFollowUpOption) {
    this.args.step.emailFollowUpType = option;
  }

  @action
  updateEmailFollowUpDuration(durationInSeconds: number) {
    this.args.step.emailFollowUpTimer = durationInSeconds;
  }

  get shouldShowPreview() {
    return this.selectedOption !== 'no_follow_up';
  }

  get previewText() {
    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.preview-text',
    );
  }

  get selectedLabel() {
    switch (this.selectedOption) {
      case 'follow_up':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.summary-follow-up',
        );
      case 'no_follow_up':
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.summary-no-follow-up',
        );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::EmailFollowUp': typeof EmailFollowUp;
    'workflows/graph-editor/node-items/steps/fin/sections/email-follow-up': typeof EmailFollowUp;
  }
}
